import React, { useState } from 'react';
import { Block, ButtonSelect, Flex, Textarea } from '@upsales/components';
import BemClass from '@upsales/components/Utils/bemClass';
import { useTranslation } from 'Components/Helpers/translate';
import EditPhonecallNotes from 'Components/EditPhonecall/EditPhonecallNotes';
import AINotesDisplay from './AINotesDisplay';
import './NotesWithAISummary.scss';

export type AINotesField = 'aiNotes' | 'aiNotesEn';

type Props = {
	notes: string;
	aiNotes?: string;
	aiNotesEn?: string;
	onNotesChange: (value: string) => void;
	onAINotesChange: (field: AINotesField, value: string) => void;
	onNotesAutoSave?: (value: string) => void;
	loading?: boolean;
	disabled?: boolean;
	hasError?: boolean;
	textAreaProps?: Partial<React.ComponentProps<typeof Textarea>>;
	textareaTooltipText?: string;
};

/**
 * The notes field, plus a toggle to the AI-generated summary when there is one. Used by both the
 * appointment modal and the phone call drawer, so a summary never lands on top of the user's own
 * notes. With no summary present this renders the plain notes field and nothing else.
 */
const NotesWithAISummary = ({
	notes,
	aiNotes = '',
	aiNotesEn = '',
	onNotesChange,
	onAINotesChange,
	onNotesAutoSave,
	loading = false,
	disabled = false,
	hasError = false,
	textAreaProps = {},
	textareaTooltipText
}: Props) => {
	const classes = new BemClass('NotesWithAISummary');
	const { t } = useTranslation();
	const [selectedNotesType, setSelectedNotesType] = useState<'regular' | 'ai'>('regular');

	const notesField = (
		<EditPhonecallNotes
			loading={loading}
			notes={notes}
			onChange={onNotesChange}
			onAutoSave={onNotesAutoSave}
			textAreaProps={textAreaProps}
			hasError={hasError}
			textareaTooltipText={textareaTooltipText}
			disabled={disabled}
		/>
	);

	if (!aiNotes?.trim().length) {
		return notesField;
	}

	return (
		<Flex direction="column" flex={1} gap="u4" className={classes.b()}>
			<Block className={classes.elem('toggle').b()}>
				<ButtonSelect
					size="sm"
					value={selectedNotesType}
					onChange={setSelectedNotesType}
					options={[
						{ value: 'regular', title: t('default.notes') },
						{ value: 'ai', title: t('default.aiSummary') }
					]}
				/>
			</Block>
			{selectedNotesType === 'regular' ? (
				notesField
			) : (
				<AINotesDisplay notes={aiNotes} notesEn={aiNotesEn} onChange={onAINotesChange} disabled={disabled} />
			)}
		</Flex>
	);
};

export default NotesWithAISummary;
